import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaFire, FaArrowUp, FaArrowDown, FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp, FaSync, FaChartLine, FaShieldAlt, FaInfoCircle, FaImage, FaChartBar, FaLock, FaSignInAlt, FaPhone, FaEnvelope, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const HeatMapDashboard = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ HeatMapDashboard: 0 });

    // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'IPO Research & Analysis',
    description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page4`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.HeatMapDashboard === 1) {
      fetchPageMetadata(); // Add this line
    }
  }, [isLoggedIn, checkingLogin, contentAccess.HeatMapDashboard]);
  // Metadata End===========================================================
  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ HeatMapDashboard: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ HeatMapDashboard: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ HeatMapDashboard: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================
  
  // ==================== ORIGINAL COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' }); // No default sort
  const [filter, setFilter] = useState('all');
  const [selectedStock, setSelectedStock] = useState(null);
  const [marketData, setMarketData] = useState({ sensex: null, sensexChange: null, nifty: null, niftyChange: null });
  const [refreshCount, setRefreshCount] = useState(0); // Track refresh count
  const [isAutoRefresh, setIsAutoRefresh] = useState(true); // Auto-refresh toggle

  // API Configuration
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1Hr1ywim8SytIFfF68v6RUN9ESoGfk7fwpNBqgj2EhYc/values/HEAT%20MAP?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';
  
  // Backend proxy URL
  const BACKEND_URL = 'https://api.marketmaestroovip.in';

  // Use ref to track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch data function
  const fetchHeatMapData = async () => {
    try {
      console.log(`Fetching data... (Auto-refresh: ${isAutoRefresh})`);
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      if (isMounted.current) {
        setData(result);
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);
        
        // Extract market indices from data
        extractMarketIndices(result.values);
        
        console.log('Data updated successfully');
      }
      
    } catch (err) {
      console.error('Error fetching heat map data:', err);
      if (isMounted.current) {
        setError(err.message);
        setLoading(false);
      }
    }
  };

  // Initial fetch - only if logged in and has access
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.HeatMapDashboard === 1) {
      fetchHeatMapData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.HeatMapDashboard]);

  // Auto-refresh effect - only if logged in and has access
  useEffect(() => {
    let intervalId;
    
    if (isLoggedIn && !checkingLogin && contentAccess.HeatMapDashboard === 1 && isAutoRefresh) {
      intervalId = setInterval(() => {
        setRefreshCount(prev => prev + 1);
      }, 60000); // 60 seconds
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLoggedIn, checkingLogin, contentAccess.HeatMapDashboard, isAutoRefresh]);

  // Fetch when refreshCount changes
  useEffect(() => {
    if (refreshCount > 0 && isAutoRefresh && isLoggedIn && contentAccess.HeatMapDashboard === 1) {
      fetchHeatMapData();
    }
  }, [refreshCount, isLoggedIn, contentAccess.HeatMapDashboard]);

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setIsAutoRefresh(!isAutoRefresh);
    if (!isAutoRefresh) {
      // If turning on, trigger immediate refresh
      setRefreshCount(prev => prev + 1);
    }
  };

  // Manual refresh
  const manualRefresh = () => {
    if (isLoggedIn && contentAccess.HeatMapDashboard === 1) {
      setRefreshCount(prev => prev + 1);
    }
  };

  // Clear all filters and sorting
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilter('all');
    setSortConfig({ key: null, direction: 'desc' });
  };

  // Extract market indices (Sensex and Nifty)
  const extractMarketIndices = (values) => {
    try {
      // Look for Sensex row (row index 4)
      if (values[4]) {
        const sensexRow = values[4];
        
        // Find Sensex value (at index 7)
        if (sensexRow[7]) {
          let sensexValue = sensexRow[7].toString();
          // Remove ▲ symbol and trim
          sensexValue = sensexValue.replace('▲', '').trim();
          setMarketData(prev => ({ ...prev, sensex: sensexValue }));
        }
        
        // Find Nifty value (at index 11)
        if (sensexRow[11]) {
          let niftyValue = sensexRow[11].toString();
          // Remove ▲ symbol and trim
          niftyValue = niftyValue.replace('▲', '').trim();
          setMarketData(prev => ({ ...prev, nifty: niftyValue }));
        }
      }
      
      // Look for change percentages row (row index 6)
      if (values[6]) {
        const changeRow = values[6];
        
        // Find Sensex change (at index 7)
        if (changeRow[7]) {
          let sensexChange = changeRow[7].toString();
          // Remove ▲ symbol and trim
          sensexChange = sensexChange.replace('▲', '').trim();
          setMarketData(prev => ({ ...prev, sensexChange: sensexChange }));
        }
        
        // Find Nifty change (at index 11)
        if (changeRow[11]) {
          let niftyChange = changeRow[11].toString();
          // Remove ▲ symbol and trim
          niftyChange = niftyChange.replace('▲', '').trim();
          setMarketData(prev => ({ ...prev, niftyChange: niftyChange }));
        }
      }
    } catch (err) {
      console.error('Error extracting market indices:', err);
    }
  };

  // Get image URL from logo column
  const getImageUrl = (logoUrl) => {
    if (!logoUrl) {
      return null;
    }
    
    // Check if it's already a full URL
    if (logoUrl.startsWith('http')) {
      return logoUrl;
    }
    
    // Check if it's a local uploads URL
    if (logoUrl.startsWith('/uploads/')) {
      return `${BACKEND_URL}${logoUrl}`;
    }
    
    return null;
  };

  // Generate simple avatar as fallback
  const getAvatarUrl = (symbolCode) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(symbolCode)}&background=random&color=fff&bold=true&size=128`;
  };

  // Process the data to extract heat map stocks with logo support
  const processHeatMapData = () => {
    if (!data || !data.values) {
      return [];
    }

    const values = data.values;
    let stocks = [];
    
    for (let i = 8; i < values.length; i++) {
      const row = values[i];
      if (!row) continue;
      
      try {
        for (let col = 0; col < row.length; col++) {
          const cell = row[col];
          
          // Check if cell contains a stock symbol (NSE: or BSE:)
          if (cell && (cell.startsWith('NSE:') || cell.startsWith('BSE:'))) {
            const stockSymbol = cell;
            
            // Get the next columns for price and change
            const priceStr = row[col + 1];
            const changePctStr = row[col + 2];
            const companyName = row[col + 3] || stockSymbol.split(':')[1];
            const logoUrl = row[col + 4];
            
            // Skip if price or change is #N/A
            if (priceStr === '#N/A' || changePctStr === '#N/A') {
              continue;
            }
            
            // Parse price and change percentage
            let price = 0;
            let changePct = 0;
            
            try {
              price = parseFloat(priceStr?.replace(/,/g, '') || 0);
              changePct = parseFloat(changePctStr?.replace('%', '')?.replace('▼', '-')?.replace('▲', '') || 0);
            } catch (e) {
              continue;
            }
            
            // Skip if parsing failed
            if (isNaN(price) || isNaN(changePct)) {
              continue;
            }
            
            // Extract symbol code
            const symbolParts = stockSymbol.split(':');
            const symbolCode = symbolParts.length > 1 ? symbolParts[1] : stockSymbol;
            
            // Get logo URL
            const imageUrl = getImageUrl(logoUrl);
            const fallbackAvatarUrl = getAvatarUrl(symbolCode);
            
            stocks.push({
              id: `${i}-${col}-${Date.now()}`, // Add timestamp for unique keys during refresh
              symbol: stockSymbol,
              code: symbolCode,
              name: companyName || symbolCode || 'Unknown',
              price: price,
              changePct: changePct,
              absoluteChange: (price * changePct) / 100,
              imageUrl: imageUrl,
              fallbackUrl: fallbackAvatarUrl,
              hasLogo: !!imageUrl,
              logoUrl: logoUrl,
              originalIndex: i // Preserve original order
            });
            
            break;
          }
        }
        
      } catch (error) {
        continue;
      }
    }
    
    return stocks;
  };

  const allStocks = useMemo(() => processHeatMapData(), [data]);

  // Filter and sort stocks - preserve original order when no sort is applied
  const filteredAndSortedStocks = useMemo(() => {
    let filtered = [...allStocks];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.symbol.toLowerCase().includes(term) ||
        stock.name.toLowerCase().includes(term) ||
        stock.code.toLowerCase().includes(term)
      );
    }
    
    // Apply gainer/loser filter
    if (filter === 'gainers') {
      filtered = filtered.filter(stock => stock.changePct > 0);
    } else if (filter === 'losers') {
      filtered = filtered.filter(stock => stock.changePct < 0);
    }
    
    // Apply sorting only if a sort key is selected
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Preserve original order when no sort is applied
      filtered.sort((a, b) => a.originalIndex - b.originalIndex);
    }
    
    return filtered;
  }, [allStocks, searchTerm, filter, sortConfig]);

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Get color classes based on change percentage - ORIGINAL COLORS PRESERVED
  const getColorClass = (changePct) => {
    if (changePct > 5) return 'bg-gradient-to-br from-green-700 to-green-700';
    if (changePct > 2) return 'bg-gradient-to-br from-green-700 to-green-700';
    if (changePct > 0) return 'bg-gradient-to-br from-green-700 to-green-700';
    if (changePct > -2) return 'bg-gradient-to-br from-red-700 to-red-700';
    if (changePct > -5) return 'bg-gradient-to-br from-red-700 to-red-700';
    return 'bg-gradient-to-br from-red-700 to-red-700';
  };

  // Handle image error
  const handleImageError = (e, stock) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = stock.fallbackUrl;
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Market Heat Map Dashboard.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Heat Map Dashboard</h1>
                    <p className="text-blue-100">Real-time stock performance visualization powered by Market Maestroo</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.HeatMapDashboard !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Market Heat Map Dashboard yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Market Heat Map Dashboard</h1>
                    <p className="text-blue-100">Real-time stock performance visualization powered by Market Maestroo</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content access restricted.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading market heat map...</p>
          <p className="text-sm text-gray-500 mt-2">Fetching live data from Google Sheets</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaFire className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Heat Map</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={manualRefresh}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
     <>
        <Header />
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                    <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                    <p className="text-blue-100">{pageMetadata.description}</p>
                    </div>
                    <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                    <div className="flex items-center gap-3 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                        <FaShieldAlt className="text-yellow-300" />
                        <span className="font-semibold">SEBI Registered Platform</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                          onClick={manualRefresh}
                          className="flex items-center gap-2 bg-white/30 hover:bg-white/40 px-4 py-2 rounded-lg transition-colors"
                      >
                          <FaSync className="text-sm" />
                          Refresh Now
                      </button>
                      <button
                          onClick={toggleAutoRefresh}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            isAutoRefresh 
                              ? 'bg-green-500 hover:bg-green-600' 
                              : 'bg-gray-500 hover:bg-gray-600'
                          }`}
                      >
                          <div className={`w-3 h-3 rounded-full ${isAutoRefresh ? 'bg-white' : 'bg-gray-300'}`}></div>
                          {isAutoRefresh ? 'Auto: ON' : 'Auto: OFF'}
                      </button>
                    </div>
                    </div>
                </div>
                </div>
            </div>

            {/* Market Indices */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    key={`sensex-${lastUpdated?.getTime()}`}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <FaChartBar className="text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700">BSE SENSEX</h3>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                        {marketData.sensex || 'Loading...'}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {marketData.sensexChange && (
                            <>
                              {parseFloat(marketData.sensexChange) >= 0 ? (
                                <FaArrowUp className="text-green-500" />
                              ) : (
                                <FaArrowDown className="text-red-500" />
                              )}
                              <span className={parseFloat(marketData.sensexChange) >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                {marketData.sensexChange} Today
                              </span>
                            </>
                          )}
                        </div>
                    </div>
                    <div className="text-4xl text-blue-100">
                        📈
                    </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    key={`nifty-${lastUpdated?.getTime()}`}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100"
                >
                    <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FaChartLine className="text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700">NIFTY 50</h3>
                        </div>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                        {marketData.nifty || 'Loading...'}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {marketData.niftyChange && (
                            <>
                              {parseFloat(marketData.niftyChange) >= 0 ? (
                                <FaArrowUp className="text-green-500" />
                              ) : (
                                <FaArrowDown className="text-red-500" />
                              )}
                              <span className={parseFloat(marketData.niftyChange) >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                {marketData.niftyChange} Today
                              </span>
                            </>
                          )}
                        </div>
                    </div>
                    <div className="text-4xl text-blue-100">
                        📊
                    </div>
                    </div>
                </motion.div>
                </div>

                {/* Controls Section */}
                <div className="mb-8 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                        type="text"
                        placeholder="Search stocks by symbol or name (e.g., RELIANCE, TCS)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                        />
                    </div>
                    </div>

                    {/* Filters */}
                    <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                        filter === 'all' 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        All Stocks
                    </button>
                    <button
                        onClick={() => setFilter('gainers')}
                        className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                        filter === 'gainers' 
                            ? 'bg-green-600 text-white shadow-sm' 
                            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        <FaArrowUp />
                        Gainers
                    </button>
                    <button
                        onClick={() => setFilter('losers')}
                        className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                        filter === 'losers' 
                            ? 'bg-red-600 text-white shadow-sm' 
                            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        <FaArrowDown />
                        Losers
                    </button>
                    <button
                        onClick={clearAllFilters}
                        className="px-4 py-3 rounded-lg transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    >
                        <FaTimes />
                        Clear Filters
                    </button>
                    </div>
                </div>

                {/* Sort Controls */}
                <div className="flex flex-wrap items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <span className="text-gray-600 font-medium">Sort by:</span>
                    <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => handleSort('changePct')}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        sortConfig.key === 'changePct' 
                            ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                        }`}
                    >
                        % Change
                        {sortConfig.key === 'changePct' && (
                        sortConfig.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                        )}
                    </button>
                    <button
                        onClick={() => handleSort('price')}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        sortConfig.key === 'price' 
                            ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                        }`}
                    >
                        Price
                        {sortConfig.key === 'price' && (
                        sortConfig.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                        )}
                    </button>
                    <button
                        onClick={() => handleSort('name')}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        sortConfig.key === 'name' 
                            ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                        }`}
                    >
                        Company Name
                        {sortConfig.key === 'name' && (
                        sortConfig.direction === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                        )}
                    </button>
                    </div>
                    
                    {sortConfig.key && (
                      <button
                        onClick={() => setSortConfig({ key: null, direction: 'desc' })}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                      >
                        <FaTimes className="text-xs" />
                        Clear Sort
                      </button>
                    )}
                    
                    <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
                    <FaInfoCircle />
                    <span>{filteredAndSortedStocks.length} stocks found</span>
                    {isAutoRefresh && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Auto-refresh ON
                      </span>
                    )}
                    </div>
                </div>
                </div>

                {/* Heat Map Grid */}
                <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
                >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <FaFire className="text-orange-500" />
                    Live Market Heat Map
                    </h2>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
                      </div>
                      {isAutoRefresh && lastUpdated && (
                        <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          Next refresh: {60 - (Math.floor((Date.now() - (lastUpdated?.getTime() || 0)) / 1000) % 60)}s
                        </div>
                      )}
                    </div>
                </div>

                {filteredAndSortedStocks.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                    <FaSearch className="text-gray-400 text-4xl mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Stocks Found</h3>
                    <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                    <div className="mt-4 space-x-2">
                      <button 
                        onClick={clearAllFilters}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Clear Filters
                      </button>
                      <button 
                        onClick={manualRefresh}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Refresh Data
                      </button>
                    </div>
                    </div>
                ) : (
                    <>
                    {/* Heat Map Visualization with Logos */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-8">
                        <AnimatePresence>
                        {filteredAndSortedStocks.map((stock, index) => (
                            <motion.div
                              key={stock.id} // Unique key with timestamp ensures proper re-render
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.3, delay: index * 0.02 }}
                              whileHover={{ scale: 1.03, y: -4 }}
                              onClick={() => setSelectedStock(stock)}
                              className={`cursor-pointer rounded-xl p-4 border-2 border-white/20 transition-all duration-300 ${getColorClass(stock.changePct)} shadow-sm hover:shadow-md`}
                            >
                              <div className="text-center">
                                  {/* Logo Section - Always shown */}
                                  <div className="mb-3 flex justify-center">
                                      <div className="relative">
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-50 to-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden mx-auto">
                                          <img 
                                            src={stock.imageUrl || stock.fallbackUrl}
                                            alt={stock.code}
                                            className="w-full object-cover"
                                            onError={(e) => handleImageError(e, stock)}
                                            loading="lazy"
                                          />
                                        </div>
                                      </div>
                                  </div>
                                  
                                  <div className="font-bold text-lg text-white truncate">
                                  {stock.name}
                                  </div>
                                  
                                  <div className="mt-3 text-xl font-bold text-white">
                                  ₹{stock.price.toLocaleString('en-IN')}
                                  </div>
                                  
                                  <div className={`mt-2 text-lg font-bold flex items-center justify-center gap-1`}>
                                  {stock.changePct > 0 ? (
                                      <FaArrowUp className="text-white" />
                                  ) : (
                                      <FaArrowDown className="text-white" />
                                  )}
                                  <span className="text-white">
                                      {stock.changePct > 0 ? '+' : ''}{stock.changePct.toFixed(2)}%
                                  </span>
                                  </div>
                                  
                                  <div className="mt-2 flex items-center justify-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${stock.changePct > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      ₹{Math.abs(stock.absoluteChange).toFixed(2)}
                                  </span>
                                  </div>
                              </div>
                            </motion.div>
                        ))}
                        </AnimatePresence>
                    </div>
                    </>
                )}
                </motion.div>

                {/* Footer & Legend */}
                <div className="mt-8">
                {/* Heat Map Legend - Only percentage ranges, NO intensity indicators */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaInfoCircle className="text-blue-500" />
                    How to Read the Heat Map
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-green-700 to-green-700 text-white">
                        <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                        <span className="font-medium">Strong Gainers</span>
                        </div>
                        <p className="text-sm opacity-90">+2% or more</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-green-700 to-green-700 text-white">
                        <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                        <span className="font-medium">Moderate Gainers</span>
                        </div>
                        <p className="text-sm opacity-90">0% to +2%</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-red-700 to-red-700 text-white">
                        <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                        <span className="font-medium">Moderate Losers</span>
                        </div>
                        <p className="text-sm opacity-90">0% to -2%</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-red-700 to-red-700 text-white">
                        <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                        <span className="font-medium">Strong Losers</span>
                        </div>
                        <p className="text-sm opacity-90">-2% or less</p>
                    </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded-full"></div>
                        <span>Auto-refresh: {isAutoRefresh ? 'Enabled (60s)' : 'Disabled'}</span>
                      </div>
                      <button 
                        onClick={toggleAutoRefresh}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {isAutoRefresh ? 'Disable' : 'Enable'} Auto-refresh
                      </button>
                    </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center text-sm text-gray-500">
                    {lastUpdated && (
                    <p className="mt-1">Data last refreshed: {lastUpdated.toLocaleString()}</p>
                    )}
                    <p className="mt-2 text-xs bg-blue-50 inline-block px-3 py-1 rounded-full">
                    Market Maestroo Private Limited | SEBI Registered Platform | Registration No: INH000021757
                    </p>
                </div>
                </div>
            </div>
            </div>
        <Footer />
    </>
  );
};

export default HeatMapDashboard;